import React from 'react'

import { __CONTAINER_INTERNALS_DO_NOT_USE__ } from '@inno-tools/container-bridge'
import { WIDGET_MARKETPLACE_WINDOW_ID } from 'modules/desktop/pages/MainPage/components/WidgetMarketplace/WidgetMarketplace.constants'
import { useWidgetMarketplaceFilterStore } from 'modules/desktop/pages/MainPage/components/WidgetMarketplace/widgetMarketplaceFilterStore'
import { useOpenWindowMarketplace } from 'modules/desktop/pages/MainPage/hooks/useOpenWindowMarketplace'
import { SnapArea } from 'shared/types/index'

import { useWindowStore } from '../../stores'
import styles from './WindowManager.module.scss'
import { Window } from './components'
import { IframeNavigationHandler } from './components/Window/utils/IframeNavigationHandler'
import { resolveMarketplaceCategory } from './resolveMarketplaceCategory'

interface Props {
  constraintsRef: React.RefObject<HTMLDivElement>
  topDockHeight: number
}

const WindowManager: React.FC<Props> = ({ constraintsRef, topDockHeight }) => {
  const windows = useWindowStore(state => state.windows)
  const closeWindow = useWindowStore(state => state.closeWindow)
  const minimizeWindow = useWindowStore(state => state.minimizeWindow)
  const setSnapArea = useWindowStore(state => state.setSnapArea)
  const setActiveWindow = useWindowStore(state => state.setActiveWindow)

  const openWidgetMarketplace = useOpenWindowMarketplace()

  __CONTAINER_INTERNALS_DO_NOT_USE__.useContainerEventListener__INTERNAL__({
    MINIMIZE_WINDOW: data => {
      minimizeWindow(data.windowId)
    },
    SNAP_WINDOW: data => {
      setSnapArea(data.windowId, data.snapArea as SnapArea)
    },
    CLOSE_WINDOW: data => {
      closeWindow(data.windowId)
    },
    OPEN_WIDGET_MARKETPLACE: data => {
      const { relatedApp, section } = resolveMarketplaceCategory(data)
      // Always publish to the filter store. If the window is already
      // open, the running component's effect consumes both slots; if
      // not, the `openWidgetMarketplace({ relatedApp, section })` call
      // below pushes again (idempotent overwrite) before mounting so
      // the component sees the right seed on first paint.
      useWidgetMarketplaceFilterStore
        .getState()
        .setPendingRelatedApp(relatedApp)
      useWidgetMarketplaceFilterStore.getState().setPendingSection(section)

      const isOpen = useWindowStore
        .getState()
        .getWindowState(WIDGET_MARKETPLACE_WINDOW_ID)
      if (isOpen) {
        setActiveWindow(WIDGET_MARKETPLACE_WINDOW_ID)
      } else {
        openWidgetMarketplace({ relatedApp, section })
      }
    }
  })

  return (
    <div className={styles.windowManager}>
      <IframeNavigationHandler />
      {windows.map(window => (
        <Window
          key={window.id}
          window={window}
          constraintsRef={constraintsRef}
          topDockHeight={topDockHeight}
        />
      ))}
    </div>
  )
}

export { WindowManager }
